import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Mail, Lock, ArrowLeft, Briefcase } from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "agent"
  });
  
  // 2FA state
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [pendingAgentId, setPendingAgentId] = useState("");
  const [otpCode, setOtpCode] = useState("");

  // Load saved credentials on mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem("therentpayer_agent_credentials");
    if (savedCredentials) {
      try {
        const { email, password, remember } = JSON.parse(savedCredentials);
        setFormData(prev => ({ ...prev, email: email || "", password: password || "" }));
        setRememberMe(remember || false);
      } catch (e) {
        // Invalid data, ignore
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Registration flow
        const response = await axios.post(`${API}/agent/register`, formData);
        localStorage.setItem("agent_token", response.data.token);
        toast.success("Account created!");
        navigate("/agent/dashboard");
      } else {
        // Login flow - check for 2FA
        const response = await axios.post(`${API}/agent/auth/request-otp`, { 
          email: formData.email, 
          password: formData.password 
        });
        
        // Save or clear credentials based on Remember Me
        if (rememberMe) {
          localStorage.setItem("therentpayer_agent_credentials", JSON.stringify({
            email: formData.email,
            password: formData.password,
            remember: true
          }));
        } else {
          localStorage.removeItem("therentpayer_agent_credentials");
        }
        
        if (response.data.two_factor_required) {
          // 2FA is enabled - show verification dialog
          setPendingAgentId(response.data.agent_id);
          setShow2FAVerify(true);
          toast.info("Enter the code from your authenticator app");
        } else {
          // No 2FA - login directly
          localStorage.setItem("agent_token", response.data.token);
          toast.success("Welcome back!");
          navigate("/agent/dashboard");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async () => {
    if (otpCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/agent/auth/verify-otp`, {
        agent_id: pendingAgentId,
        otp: otpCode
      });
      localStorage.setItem("agent_token", response.data.token);
      toast.success("Welcome back!");
      navigate("/agent/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-foreground flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Main Site
        </Link>

        <Card className="bg-white/5 border-white/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-primary p-3 rounded-full">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Young Serif, serif' }}>
              {isRegister ? "Agent Registration" : "Agent Portal"}
            </CardTitle>
            <CardDescription className="text-white/60">
              {isRegister ? "Create your agent account" : "Sign in to manage policies and claims"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-white/80">Full Name</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                    data-testid="agent-name-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="agent@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                    data-testid="agent-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    required
                    data-testid="agent-password-input"
                  />
                </div>
              </div>

              {!isRegister && (
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember-me-agent" 
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className="border-white/40 data-[state=checked]:bg-primary"
                    data-testid="agent-remember-me-checkbox"
                  />
                  <Label 
                    htmlFor="remember-me-agent" 
                    className="text-sm font-normal text-white/70 cursor-pointer"
                  >
                    Remember my email and password
                  </Label>
                </div>
              )}

              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-white/80">Role</Label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                    data-testid="agent-role-select"
                  >
                    <option value="agent" className="bg-foreground">Agent</option>
                    <option value="claims_processor" className="bg-foreground">Claims Processor</option>
                    <option value="admin" className="bg-foreground">Admin</option>
                  </select>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full py-6 bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="agent-submit-btn"
              >
                {loading ? "Please wait..." : (isRegister ? "Create Account" : "Sign In")}
              </Button>
            </form>

            <p className="text-center text-sm text-white/60 mt-6">
              {isRegister ? "Already have an account?" : "Need an account?"}{" "}
              <button 
                onClick={() => setIsRegister(!isRegister)}
                className="text-primary hover:underline font-medium"
              >
                {isRegister ? "Sign in" : "Register"}
              </button>
            </p>
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center justify-center gap-2">
          <NestIcon className="h-5 w-5 text-white/40" />
          <span className="text-sm text-white/40">TheRentPayer Agent Portal</span>
        </div>
      </div>

      {/* 2FA Verification Dialog */}
      {show2FAVerify && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-card">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-primary p-3 rounded-full">
                  <Lock className="h-8 w-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'Young Serif, serif' }}>
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp-code">Verification Code</Label>
                <Input
                  id="otp-code"
                  type="text"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  autoFocus
                  data-testid="agent-2fa-input"
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShow2FAVerify(false);
                    setPendingAgentId("");
                    setOtpCode("");
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-primary hover:bg-primary/90"
                  onClick={verify2FA}
                  disabled={loading || otpCode.length !== 6}
                  data-testid="agent-2fa-verify-btn"
                >
                  {loading ? "Verifying..." : "Verify"}
                </Button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground">
                Open your authenticator app (Google Authenticator, Authy, etc.) to get your code
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
